/**
 * ItemFormFields Column 2 - Dates / Details and Dates
 *
 * Renders fields for the middle column based on item type.
 * Layout varies by type.
 */

import { useState, useEffect, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { Input, TextArea, Select, DatePicker, Button } from '@/components/ui';
import { Plus, X, Link as LinkIcon, ExternalLink, Bell, Clock } from 'lucide-react';
import { useUIStore, useItemStore } from '@/stores';
import type { UseFormReturn } from 'react-hook-form';
import type { ItemType, Reminder, AiSummary } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

interface ItemFormFieldsCol2Props {
  type: ItemType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  disabled?: boolean;
  statusOnlyEditable?: boolean;
  isSubItem?: boolean;
  fullWebPageHTML?: string;
  sourceUrl?: string;
  createdAtDisplay?: string;
  aiSummary?: AiSummary;
  itemId?: string;
  itemCreatedBy?: string;
}

// ============================================================================
// OPTIONS
// ============================================================================

const recurrenceOptions = [
  { value: 'none', label: 'None' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const daysOfWeekLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Ordinal patterns for monthly recurrence
const ordinalDays = ['First', 'Second', 'Third', 'Fourth'];
const weekdayLabels = ['Sat', 'Sun'];

// Generate all ordinal day options: "First Sat", "First Sun", "Second Sat", etc.
const ordinalPatterns = ordinalDays.flatMap(ordinal =>
  weekdayLabels.map(day => `${ordinal} ${day}`)
);

const daysBeforeOptions = [
  { value: '0', label: 'Same day' },
  { value: '1', label: '1 day before' },
  { value: '2', label: '2 days before' },
  { value: '3', label: '3 days before' },
  { value: '7', label: '1 week before' },
  { value: '14', label: '2 weeks before' },
  { value: '30', label: '1 month before' },
];

const colorOptions = [
  { value: 'default', label: 'Default', bg: 'bg-white', border: 'border-gray-300' },
  { value: 'gray', label: 'Gray', bg: 'bg-gray-200', border: 'border-gray-300' },
  { value: 'red', label: 'Red', bg: 'bg-red-200', border: 'border-red-300' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-200', border: 'border-orange-300' },
  { value: 'amber', label: 'Amber', bg: 'bg-amber-200', border: 'border-amber-300' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-200', border: 'border-yellow-300' },
  { value: 'lime', label: 'Lime', bg: 'bg-lime-200', border: 'border-lime-300' },
  { value: 'green', label: 'Green', bg: 'bg-green-200', border: 'border-green-300' },
  { value: 'teal', label: 'Teal', bg: 'bg-teal-200', border: 'border-teal-300' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-200', border: 'border-cyan-300' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-200', border: 'border-blue-300' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-200', border: 'border-indigo-300' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-200', border: 'border-purple-300' },
  { value: 'pink', label: 'Pink', bg: 'bg-pink-200', border: 'border-pink-300' },
];

// ============================================================================
// HELPERS
// ============================================================================

function generateReminderId(): string {
  return `rem_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function normalizeUrl(url: string): string {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

// ============================================================================
// AI SUMMARY PAGE HELPER
// Supports two modes:
//   'create' — generates AI summary via Gemini Prompt API, shows Add to Note
//   'view'   — displays a previously saved summary, no Add to Note
// ============================================================================

interface AiSummaryPageOptions {
  mode: 'create' | 'view';
  fullWebPageHTML?: string;      // required for 'create'
  savedSummaryContent?: string;  // required for 'view'
  summaryTitle: string;
  sourceUrl?: string;
  createdAtDisplay?: string;
}

function buildSummaryPageStyles(): string {
  return `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Georgia, 'Times New Roman', serif; background: #fff; color: #111; padding: 40px 24px; max-width: 720px; margin: 0 auto; line-height: 1.7; }
  .label { font-size: 16px; font-weight: 400; color: #2563eb; margin-bottom: 6px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  h1 { font-size: 24px; font-weight: 700; margin-bottom: 6px; color: #111; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  .source-url { font-size: 13px; margin-bottom: 4px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  .source-url a { color: #2563eb; text-decoration: none; }
  .source-url a:hover { text-decoration: underline; }
  .created-date { font-size: 13px; color: #111; margin-bottom: 24px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  .toolbar { margin-bottom: 32px; display: flex; gap: 10px; flex-wrap: wrap; }
  .toolbar button { display: none; padding: 8px 20px; font-size: 14px; font-weight: 600; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  #add-btn { background: #ea580c; }
  #add-btn:hover { background: #c2410c; }
  #add-btn.added { background: #16a34a; cursor: default; }
  #print-btn { background: #7c3aed; }
  #print-btn:hover { background: #6d28d9; }
  #copy-btn { background: #2563eb; }
  #copy-btn:hover { background: #1d4ed8; }
  #status { font-size: 15px; color: #555; margin-bottom: 24px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  #status.error { color: #b91c1c; }
  #summary { font-size: 17px; white-space: pre-wrap; }
  @media print { .toolbar { display: none !important; } }`;
}

function buildSummaryPageScript(mode: 'create' | 'view', textForAi: string, truncatedTitle: string, pageTitle: string, sourceUrl: string, createdAt: string, savedContent: string): string {
  return `
const MODE = ${JSON.stringify(mode)};
const TEXT = ${JSON.stringify(textForAi)};
const SAVED_CONTENT = ${JSON.stringify(savedContent)};
const TITLE_SLUG = ${JSON.stringify(truncatedTitle)};
const PAGE_TITLE = ${JSON.stringify(pageTitle)};
const SOURCE_URL = ${JSON.stringify(sourceUrl)};
const CREATED_AT = ${JSON.stringify(createdAt)};

const statusEl = document.getElementById('status');
const summaryEl = document.getElementById('summary');
const addBtn = document.getElementById('add-btn');
const printBtn = document.getElementById('print-btn');
const copyBtn = document.getElementById('copy-btn');

function showError(msg) {
  statusEl.className = 'error';
  statusEl.textContent = msg;
}

function showStatus(msg) {
  statusEl.className = '';
  statusEl.textContent = msg;
}

function getFormattedDateTime() {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yyyy = now.getFullYear();
  let hh = now.getHours();
  const min = String(now.getMinutes()).padStart(2, '0');
  const ampm = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12 || 12;
  return mm + '-' + dd + '-' + yyyy + '-' + String(hh).padStart(2, '0') + '-' + min + '-' + ampm;
}

function showButtons() {
  if (MODE === 'create' && addBtn) addBtn.style.display = 'inline-block';
  printBtn.style.display = 'inline-block';
  copyBtn.style.display = 'inline-block';
}

printBtn.addEventListener('click', function() {
  const filename = 'AI_Summary_' + TITLE_SLUG + '_' + getFormattedDateTime();
  document.title = filename;
  window.print();
});

copyBtn.addEventListener('click', function() {
  const parts = ['AI Summary'];
  parts.push(PAGE_TITLE);
  if (SOURCE_URL) parts.push(SOURCE_URL);
  if (CREATED_AT) parts.push(CREATED_AT);
  parts.push('');
  parts.push(summaryEl.textContent.trim());
  navigator.clipboard.writeText(parts.join('\\n')).then(function() {
    const orig = copyBtn.textContent;
    copyBtn.textContent = 'Copied!';
    setTimeout(function() { copyBtn.textContent = orig; }, 2000);
  });
});

if (addBtn) {
  addBtn.addEventListener('click', function() {
    if (addBtn.classList.contains('added')) return;
    const summaryData = {
      label: 'AI Summary',
      summaryTitle: PAGE_TITLE,
      summaryDateTimeUTC: new Date().toISOString(),
      summaryContent: summaryEl.textContent.trim(),
    };
    if (window.opener) {
      window.opener.postMessage({ type: 'PERFIN_AI_SUMMARY', data: summaryData }, '*');
      addBtn.textContent = 'Added to Note!';
      addBtn.classList.add('added');
    } else {
      addBtn.textContent = 'Error: parent closed';
    }
  });
}

if (MODE === 'view') {
  statusEl.style.display = 'none';
  summaryEl.textContent = SAVED_CONTENT;
  showButtons();
} else {
  (async function() {
    if (!TEXT || TEXT.length < 50) {
      showError('Not enough text content to summarize.');
      return;
    }
    const LM = self.LanguageModel;
    if (!LM) {
      showError('Gemini Prompt API (LanguageModel) is not available in this browser. Use Chrome 131+ with Gemini Nano enabled.');
      return;
    }
    let availability;
    try { availability = await LM.availability(); }
    catch (err) { showError('Failed to check AI availability: ' + err.message); return; }
    if (availability === 'downloadable') {
      showStatus('AI model is downloading... This may take a few minutes on first use.');
      try { await LM.create(); }
      catch(e) { showError('Model download failed: ' + e.message); return; }
    } else if (availability !== 'available') {
      showError('AI model is not available (status: ' + availability + '). Enable Gemini Nano in chrome://flags.');
      return;
    }
    showStatus('Generating summary...');
    let session;
    try {
      session = await LM.create({
        systemPrompt: 'You are a helpful assistant. Summarize the given webpage text clearly and concisely. Use 3-5 paragraphs. Write in plain English. Focus on the key points, main arguments, and important details.',
      });
    } catch (err) { showError('Failed to create AI session: ' + err.message); return; }
    try {
      const result = await session.prompt('Summarize this webpage content:\\n\\n' + TEXT);
      session.destroy();
      statusEl.style.display = 'none';
      summaryEl.textContent = result.trim();
      showButtons();
    } catch (err) {
      session.destroy();
      showError('AI summarization failed: ' + err.message);
    }
  })();
}`;
}

export function openAiSummaryPage(opts: AiSummaryPageOptions): void {
  const safeSourceUrl = opts.sourceUrl || '';
  const safeCreatedAt = opts.createdAtDisplay || '';
  let pageTitle = opts.summaryTitle || 'Untitled Page';
  let textForAi = '';

  if (opts.mode === 'create' && opts.fullWebPageHTML) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(opts.fullWebPageHTML, 'text/html');
    doc.querySelectorAll('script, style, noscript, nav, header, footer, aside, [role="navigation"], [role="banner"], [role="contentinfo"]')
      .forEach(el => el.remove());
    const contentEl = doc.querySelector('article') || doc.querySelector('main') || doc.body;
    const extracted = contentEl?.innerText?.trim() || contentEl?.textContent?.trim() || '';
    textForAi = extracted.slice(0, 12000);
    pageTitle = doc.querySelector('title')?.textContent || pageTitle;
  }

  const truncatedTitle = pageTitle.replace(/[^a-zA-Z0-9 _-]/g, '').trim().slice(0, 25).trim().replace(/\s+/g, '_');
  const savedContent = opts.mode === 'view' ? (opts.savedSummaryContent || '') : '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>AI Summary — ${pageTitle.replace(/"/g, '&quot;')}</title>
<style>${buildSummaryPageStyles()}</style>
</head>
<body>
<div class="label">AI Summary</div>
<h1>${pageTitle.replace(/</g, '&lt;')}</h1>
${safeSourceUrl ? `<div class="source-url"><a href="${safeSourceUrl.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${safeSourceUrl.replace(/</g, '&lt;')}</a></div>` : ''}
${safeCreatedAt ? `<div class="created-date">${safeCreatedAt.replace(/</g, '&lt;')}</div>` : ''}
<div class="toolbar">${opts.mode === 'create' ? '<button id="add-btn">Add to Note</button>' : ''}<button id="print-btn">Download PDF</button><button id="copy-btn">Copy to Clipboard</button></div>
<div id="status">${opts.mode === 'view' ? '' : 'Checking AI availability...'}</div>
<div id="summary"></div>
<script>${buildSummaryPageScript(opts.mode, textForAi, truncatedTitle, pageTitle, safeSourceUrl, safeCreatedAt, savedContent)}<\\/script>
</body>
</html>`.replace('<\\/script>', '</script>');

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ItemFormFieldsCol2({ type, form, disabled = false, statusOnlyEditable = false, isSubItem = false, fullWebPageHTML, sourceUrl, createdAtDisplay, aiSummary, itemId, itemCreatedBy }: ItemFormFieldsCol2Props) {
  const { register, watch, setValue, control, formState: { errors } } = form;

  // When statusOnlyEditable is true, all fields in this column are disabled
  const fieldDisabled = disabled || statusOnlyEditable;
  const { showConfirm } = useUIStore();
  const { editItem } = useItemStore();

  // Local state for aiSummary — updates immediately on "Add to Note"
  const [localAiSummary, setLocalAiSummary] = useState<AiSummary | undefined>(aiSummary);

  // Sync with prop when item reloads
  useEffect(() => {
    setLocalAiSummary(aiSummary);
  }, [aiSummary]);

  // Listen for "Add to Note" postMessage from the AI summary create page
  const handleAiSummaryMessage = useCallback((event: MessageEvent) => {
    if (event.data?.type !== 'PERFIN_AI_SUMMARY') return;
    const data = event.data.data as AiSummary;
    if (!data?.summaryContent || !itemId || !itemCreatedBy) return;
    setLocalAiSummary(data);
    editItem(itemCreatedBy, itemId, { aiSummary: data }).catch((err) => {
      console.error('Failed to save aiSummary to Firestore:', err);
    });
  }, [itemId, itemCreatedBy, editItem]);

  useEffect(() => {
    window.addEventListener('message', handleAiSummaryMessage);
    return () => window.removeEventListener('message', handleAiSummaryMessage);
  }, [handleAiSummaryMessage]);

  // State for URLs
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);

  // State for Reminders
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [reminderDaysBefore, setReminderDaysBefore] = useState('1');
  const [reminderTime, setReminderTime] = useState('09:00');

  // State for Tags (notes)
  const [tagInput, setTagInput] = useState('');

  // State for Yearly dates (month 1-12, day 1-31)
  const [yearlyMonth, setYearlyMonth] = useState(1);
  const [yearlyDay, setYearlyDay] = useState(1);

  // State for custom day of month picker
  const [customDayOfMonth, setCustomDayOfMonth] = useState(2);

  // Watch values
  const recurrence = watch('recurrence');
  const isChoreWheel = watch('choreWheel');
  const selectedDaysOfWeek = watch('daysOfWeek') || [];
  const selectedDaysOfMonth = watch('daysOfMonth') || [];
  const selectedDatesOfYear = watch('datesOfYear') || [];
  const endDateForever = watch('endDateForever') || false;
  const scheduleLater = watch('scheduleLater') || false;
  const urls = watch('urls') || [];
  const reminders = watch('reminders') || [];
  const selectedColor = watch('color') || 'default';
  const tags = watch('tags') || [];

  const isRecurring = recurrence && recurrence !== 'none';

  // Handle recurrence change with chore wheel warning and date defaults
  const handleRecurrenceChange = (newValue: string) => {
    if (newValue === 'none' && isChoreWheel) {
      showConfirm({
        title: 'Remove from Chore Wheel',
        message: 'If you remove recurrence, this item will be removed from chore wheel. Do you wish to proceed?',
        cancelText: 'Cancel',
        confirmText: 'Proceed',
        onConfirm: () => {
          setValue('recurrence', 'none');
          setValue('choreWheel', false);
        },
      });
    } else {
      setValue('recurrence', newValue);
      // Default startDate and endDate to today when enabling recurrence
      if (newValue !== 'none') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const currentStartDate = form.getValues('startDate');
        const currentEndDate = form.getValues('endDate');
        if (!currentStartDate) {
          setValue('startDate', today);
        }
        if (!currentEndDate) {
          setValue('endDate', today);
        }
      }
    }
  };

  // Handlers
  const handleDayOfWeekToggle = (dayIndex: number) => {
    const current = selectedDaysOfWeek;
    if (current.includes(dayIndex)) {
      setValue('daysOfWeek', current.filter((d: number) => d !== dayIndex));
    } else {
      setValue('daysOfWeek', [...current, dayIndex].sort((a: number, b: number) => a - b));
    }
  };

  const handleDayOfMonthToggle = (day: number | string) => {
    const current = selectedDaysOfMonth;
    const dayStr = String(day);
    const exists = current.some((d: number | string) => String(d) === dayStr);
    if (exists) {
      setValue('daysOfMonth', current.filter((d: number | string) => String(d) !== dayStr));
    } else {
      // Sort: numbers first (ascending), then 'last', then ordinal patterns
      const newValue = [...current, day].sort((a: number | string, b: number | string) => {
        const aNum = typeof a === 'number' ? a : (a === 'last' ? 32 : 33);
        const bNum = typeof b === 'number' ? b : (b === 'last' ? 32 : 33);
        return aNum - bNum;
      });
      setValue('daysOfMonth', newValue);
    }
  };

  const isDayOfMonthSelected = (day: number | string): boolean => {
    return selectedDaysOfMonth.some((d: number | string) => String(d) === String(day));
  };

  // Quick days that always show (standard buttons)
  const quickDays: number[] = [1, 5, 10, 15, 20, 25];

  // Get custom selected days (numbers not in quickDays)
  const customSelectedDays: number[] = selectedDaysOfMonth
    .filter((d: number | string): d is number => typeof d === 'number' && !quickDays.includes(d))
    .sort((a: number, b: number) => a - b);

  // Handle custom day stepper
  const handleCustomDayChange = (delta: number) => {
    let newDay = customDayOfMonth + delta;
    // Skip quick days when stepping
    while (quickDays.includes(newDay) && newDay >= 2 && newDay <= 31) {
      newDay += delta;
    }
    // Clamp to valid range (2-31, excluding quick days)
    if (newDay < 2) newDay = 31;
    if (newDay > 31) newDay = 2;
    // Skip quick days after wrapping
    while (quickDays.includes(newDay)) {
      newDay += delta > 0 ? 1 : -1;
      if (newDay < 2) newDay = 31;
      if (newDay > 31) newDay = 2;
    }
    setCustomDayOfMonth(newDay);
  };

  const handleAddCustomDay = () => {
    if (!isDayOfMonthSelected(customDayOfMonth)) {
      handleDayOfMonthToggle(customDayOfMonth);
    }
  };

  // Month names for yearly recurrence
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const handleYearlyMonthChange = (delta: number) => {
    let newMonth = yearlyMonth + delta;
    if (newMonth < 1) newMonth = 12;
    if (newMonth > 12) newMonth = 1;
    setYearlyMonth(newMonth);
  };

  const handleYearlyDayChange = (delta: number) => {
    let newDay = yearlyDay + delta;
    if (newDay < 1) newDay = 31;
    if (newDay > 31) newDay = 1;
    setYearlyDay(newDay);
  };

  const handleAddYearlyDate = () => {
    const mmdd = `${String(yearlyMonth).padStart(2, '0')}-${String(yearlyDay).padStart(2, '0')}`;
    if (!selectedDatesOfYear.includes(mmdd)) {
      const newDates = [...selectedDatesOfYear, mmdd].sort();
      setValue('datesOfYear', newDates);
    }
  };

  const handleRemoveYearlyDate = (date: string) => {
    setValue('datesOfYear', selectedDatesOfYear.filter((d: string) => d !== date));
  };

  const formatYearlyDate = (mmdd: string): string => {
    const [month, day] = mmdd.split('-').map(Number);
    return `${monthNames[month - 1]} ${day}`;
  };

  const isYearlyDateSelected = (): boolean => {
    const mmdd = `${String(yearlyMonth).padStart(2, '0')}-${String(yearlyDay).padStart(2, '0')}`;
    return selectedDatesOfYear.includes(mmdd);
  };

  const handleAddUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setUrlError('Please enter a URL');
      return;
    }
    const normalized = normalizeUrl(trimmed);
    if (!isValidUrl(normalized)) {
      setUrlError('Please enter a valid URL');
      return;
    }
    if (urls.includes(normalized)) {
      setUrlError('This URL has already been added');
      return;
    }
    setValue('urls', [...urls, normalized]);
    setUrlInput('');
    setUrlError(null);
  };

  const handleRemoveUrl = (index: number) => {
    setValue('urls', urls.filter((_: string, i: number) => i !== index));
  };

  const handleUrlKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddUrl();
    }
  };

  const handleAddReminder = () => {
    const newReminder: Reminder = {
      id: generateReminderId(),
      daysBefore: parseInt(reminderDaysBefore, 10),
      time: reminderTime,
    };
    setValue('reminders', [...reminders, newReminder]);
    setShowAddReminder(false);
    setReminderDaysBefore('1');
    setReminderTime('09:00');
  };

  const handleRemoveReminder = (reminderId: string) => {
    setValue('reminders', reminders.filter((r: Reminder) => r.id !== reminderId));
  };

  /**
   * Handle Schedule Later toggle with:
   * - Confirmation modal if checking with existing reminders (they'll be cleared)
   * - Auto-populate dueDate with today when unchecking
   * - Close reminder add form when checking
   */
  const handleScheduleLaterChange = (checked: boolean) => {
    if (checked) {
      // Checking "Schedule Later" — if reminders exist, confirm first
      if (reminders.length > 0) {
        showConfirm({
          title: 'Unschedule Item',
          message: `You have ${reminders.length} reminder${reminders.length > 1 ? 's' : ''} set. Scheduling later will remove all reminders since they require a due date. Do you want to proceed?`,
          confirmText: 'Proceed',
          cancelText: 'Cancel',
          variant: 'warning',
          onConfirm: () => {
            setValue('scheduleLater', true);
            setValue('dueDate', null);
            setValue('reminders', []);
            setShowAddReminder(false);
          },
        });
      } else {
        setValue('scheduleLater', true);
        setValue('dueDate', null);
        setShowAddReminder(false);
      }
    } else {
      // Unchecking "Schedule Later" — populate dueDate with today
      setValue('scheduleLater', false);
      setValue('dueDate', new Date());
    }
  };

  const formatReminder = (reminder: Reminder): string => {
    const daysText = reminder.daysBefore === 0
      ? 'Same day'
      : reminder.daysBefore === 1
        ? '1 day before'
        : `${reminder.daysBefore} days before`;
    return `${daysText} at ${reminder.time || '09:00'}`;
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setValue('tags', [...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setValue('tags', tags.filter((t: string) => t !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const showRecurrence = type !== 'note' && !isSubItem;
  const showUrls = type !== 'keyDate';
  const dateLabel = type === 'keyDate' ? 'Date' : 'Due Date';

  // URLs section - rendered in different positions based on type
  const renderUrlsSection = () => {
    if (!showUrls) return null;
    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          <LinkIcon className="inline h-4 w-4 mr-1" />
          URLs
        </label>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              placeholder="https://example.com"
              value={urlInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUrlInput(e.target.value);
                setUrlError(null);
              }}
              onKeyDown={handleUrlKeyDown}
              disabled={fieldDisabled}
              error={urlError || undefined}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleAddUrl}
            disabled={fieldDisabled || !urlInput.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {urls.length > 0 && (
          <div className="space-y-2">
            {urls.map((url: string, index: number) => (
              <div
                key={`${url}-${index}`}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group"
              >
                <LinkIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-sm text-primary-600 hover:text-primary-800 truncate"
                >
                  {url}
                </a>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                {!fieldDisabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveUrl(index)}
                    className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Transaction: Name + Description at top of Col2 */}
      {type === 'transaction' && (
        <>
          <Input
            label="Name"
            placeholder="Enter name..."
            disabled={fieldDisabled}
            error={errors.name?.message as string | undefined}
            {...register('name', { required: 'Name is required' })}
          />
          <TextArea
            label="Description"
            placeholder="Add details..."
            rows={3}
            disabled={fieldDisabled}
            {...register('description')}
          />
        </>
      )}

      {/* Note: Color, Tags, Content at top */}
      {type === 'note' && (
        <>
          {/* Color Picker */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Color</label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  disabled={fieldDisabled}
                  onClick={() => setValue('color', color.value)}
                  className={`
                    w-8 h-8 flex-shrink-0 rounded-full border-2 transition-all
                    ${color.bg}
                    ${selectedColor === color.value
                      ? 'ring-2 ring-primary-500 ring-offset-2'
                      : color.border
                    }
                    ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}
                  `}
                  title={color.label}
                />
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Tags</label>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add tag..."
                disabled={fieldDisabled}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAddTag}
                disabled={fieldDisabled || !tagInput.trim()}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {tag}
                    {!fieldDisabled && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* View Full Page | View AI Summary (button) | Create AI Summary */}
          {fullWebPageHTML && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const blob = new Blob([fullWebPageHTML], { type: 'text/html' });
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank');
                  setTimeout(() => URL.revokeObjectURL(url), 5000);
                }}
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                View Full Page
              </button>
              <button
                type="button"
                disabled={!localAiSummary?.summaryContent}
                onClick={() => {
                  if (!localAiSummary?.summaryContent) return;
                  openAiSummaryPage({
                    mode: 'view',
                    savedSummaryContent: localAiSummary.summaryContent,
                    summaryTitle: localAiSummary.summaryTitle,
                    sourceUrl,
                    createdAtDisplay,
                  });
                }}
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg border transition-colors ${
                  localAiSummary?.summaryContent
                    ? 'bg-white border-purple-400 text-purple-700 hover:bg-purple-50 cursor-pointer'
                    : 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
              >
                View AI Summary
              </button>
              <button
                type="button"
                onClick={() => openAiSummaryPage({
                  mode: 'create',
                  fullWebPageHTML,
                  summaryTitle: form.getValues('name') || 'Untitled',
                  sourceUrl,
                  createdAtDisplay,
                })}
                className="inline-flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                Create AI Summary
              </button>
            </div>
          )}

          {/* Content */}
          <TextArea
            label="Content"
            placeholder="Write your note content..."
            rows={6}
            disabled={fieldDisabled}
            {...register('content')}
          />

          {/* URLs for Notes - before Due Date */}
          {renderUrlsSection()}
        </>
      )}

      {/* Recurrence Dropdown (for non-note types) */}
      {showRecurrence && (
        <Select
          label="Recurrence"
          options={recurrenceOptions}
          disabled={fieldDisabled}
          value={recurrence}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleRecurrenceChange(e.target.value)}
        />
      )}

      {/* Date Fields - conditional based on type and recurrence */}
      {showRecurrence ? (
        /* Non-note types: conditional based on recurrence */
        !isRecurring ? (
          /* Non-recurring: single Due Date with Schedule Later option */
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-medium text-gray-700">{dateLabel}</label>
              <label className="flex items-center gap-1.5 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={scheduleLater}
                  onChange={(e) => handleScheduleLaterChange(e.target.checked)}
                  disabled={fieldDisabled}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Schedule Later
              </label>
            </div>
            <Controller
              name="dueDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value}
                  onChange={field.onChange}
                  disabled={fieldDisabled || scheduleLater}
                />
              )}
            />
          </div>
        ) : (
          /* Recurring: Start Date + End Date with None checkbox */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Start Date"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={fieldDisabled}
                />
              )}
            />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <label className="flex items-center gap-1.5 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={endDateForever}
                    onChange={(e) => setValue('endDateForever', e.target.checked)}
                    disabled={fieldDisabled}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  None
                </label>
              </div>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    disabled={fieldDisabled || endDateForever}
                  />
                )}
              />
            </div>
          </div>
        )
      ) : (
        /* Note type / SubItem: just Due Date (no recurrence) with Schedule Later option */
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-sm font-medium text-gray-700">{dateLabel}</label>
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={scheduleLater}
                onChange={(e) => handleScheduleLaterChange(e.target.checked)}
                disabled={fieldDisabled}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Schedule Later
            </label>
          </div>
          <Controller
            name="dueDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                disabled={fieldDisabled || scheduleLater}
              />
            )}
          />
        </div>
      )}

      {/* Days of Week - shown when recurrence is weekly */}
      {showRecurrence && recurrence === 'weekly' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Repeat on</label>
          <div className="flex gap-2">
            {daysOfWeekLabels.map((label, index) => (
              <button
                key={index}
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleDayOfWeekToggle(index)}
                className={`
                  w-10 h-10 rounded-full text-xs font-medium transition-colors
                  ${selectedDaysOfWeek.includes(index)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                  ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Days of Month - shown when recurrence is monthly */}
      {showRecurrence && recurrence === 'monthly' && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Repeat on</label>
          {/* Quick select: common days + custom selected days */}
          <div className="flex flex-wrap gap-2">
            {quickDays.map((day) => (
              <button
                key={day}
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleDayOfMonthToggle(day)}
                className={`
                  px-3 py-1 rounded text-sm font-medium transition-colors
                  ${isDayOfMonthSelected(day)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                  ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {day}
              </button>
            ))}
            {/* Custom selected days (only shown when selected, disappear when clicked) */}
            {customSelectedDays.map((day: number) => (
              <button
                key={`custom-${day}`}
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleDayOfMonthToggle(day)}
                className={`
                  px-3 py-1 rounded text-sm font-medium transition-colors
                  bg-primary-600 text-white hover:bg-primary-700
                  ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {day}
              </button>
            ))}
            <button
              type="button"
              disabled={fieldDisabled}
              onClick={() => handleDayOfMonthToggle('last')}
              className={`
                px-3 py-1 rounded text-sm font-medium transition-colors
                ${isDayOfMonthSelected('last')
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
                ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              Last
            </button>
          </div>
          {/* Ordinal patterns */}
          <div className="flex flex-wrap gap-2">
            {ordinalPatterns.map((pattern) => (
              <button
                key={pattern}
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleDayOfMonthToggle(pattern)}
                className={`
                  px-3 py-1 rounded text-sm font-medium transition-colors
                  ${isDayOfMonthSelected(pattern)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                  ${fieldDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {pattern}
              </button>
            ))}
          </div>
          {/* Custom day picker */}
          <div className="flex items-center gap-3">
            <div className="flex items-center">
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleCustomDayChange(-1)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-l text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                −
              </button>
              <span className="px-4 py-1 bg-gray-100 text-gray-700 text-sm font-medium min-w-[3rem] text-center">
                {customDayOfMonth}
              </span>
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleCustomDayChange(1)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-r text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                +
              </button>
            </div>
            <button
              type="button"
              disabled={fieldDisabled || isDayOfMonthSelected(customDayOfMonth)}
              onClick={handleAddCustomDay}
              className="px-4 py-1 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Dates of Year - shown when recurrence is yearly */}
      {showRecurrence && recurrence === 'yearly' && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Repeat on dates</label>
          {/* Selected dates row (flex wrap) */}
          <div className="flex flex-wrap gap-2 min-h-[2rem]">
            {selectedDatesOfYear.map((date: string) => (
              <span
                key={date}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 text-sm rounded-full"
              >
                {formatYearlyDate(date)}
                {!fieldDisabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveYearlyDate(date)}
                    className="hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
          {/* Month and Day steppers */}
          <div className="flex items-center gap-3">
            {/* Month stepper */}
            <div className="flex items-center">
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleYearlyMonthChange(-1)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-l text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                −
              </button>
              <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium min-w-[3rem] text-center">
                {monthNames[yearlyMonth - 1]}
              </span>
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleYearlyMonthChange(1)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-r text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                +
              </button>
            </div>
            {/* Day stepper */}
            <div className="flex items-center">
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleYearlyDayChange(-1)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-l text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                −
              </button>
              <span className="px-4 py-1 bg-gray-100 text-gray-700 text-sm font-medium min-w-[3rem] text-center">
                {yearlyDay}
              </span>
              <button
                type="button"
                disabled={fieldDisabled}
                onClick={() => handleYearlyDayChange(1)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-r text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                +
              </button>
            </div>
            {/* Add button */}
            <button
              type="button"
              disabled={fieldDisabled || isYearlyDateSelected()}
              onClick={handleAddYearlyDate}
              className="px-4 py-1 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Reminders */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            <Bell className="inline h-4 w-4 mr-1" />
            Reminders
          </label>
          {!fieldDisabled && !showAddReminder && !scheduleLater && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAddReminder(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          )}
        </div>

        {scheduleLater && reminders.length === 0 && (
          <p className="text-xs text-gray-400 italic">Set a due date to add reminders</p>
        )}

        {showAddReminder && (
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                label="Days before"
                options={daysBeforeOptions}
                value={reminderDaysBefore}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setReminderDaysBefore(e.target.value)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time (CST/CDT)</label>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddReminder(false)}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleAddReminder}>
                Add Reminder
              </Button>
            </div>
          </div>
        )}

        {reminders.length > 0 && (
          <div className="space-y-2">
            {reminders.map((reminder: Reminder) => (
              <div
                key={reminder.id}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group"
              >
                <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="flex-1 text-sm text-gray-700">{formatReminder(reminder)}</span>
                {reminder.sent && (
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">Sent</span>
                )}
                {!fieldDisabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveReminder(reminder.id)}
                    className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* URLs for Task and Transaction - after Reminders */}
      {(type === 'task' || type === 'transaction') && renderUrlsSection()}
    </div>
  );
}
